import { GameState } from './types';
import { canPour, applyPour, createState, isWon } from './game';
import {
  computeLayout, renderFrame, readTheme,
  tubeAtPoint, winBtnBounds, undoBtnCenter, restartBtnCenter, menuRects,
  type Layout,
} from './renderer';
import { attachTapListener } from './input';
import { track } from './analytics';
import { t } from './i18n';
import { SCENES } from './scenes';
import { sceneId, track as ctrack, canInstall, promptInstall } from './campaign';

// ── Audio (Web Audio API) ─────────────────────────────────────────────────
const audioCtx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
const resumeAudio = () => { audioCtx.resume(); };
document.addEventListener('touchstart', resumeAudio, { once: true });
document.addEventListener('click',      resumeAudio, { once: true });

function playTone(freq: number, duration: number, volume = 0.18): void {
  if (audioCtx.state === 'suspended') return;
  try {
    const osc  = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(volume, audioCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + duration);
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.start();
    osc.stop(audioCtx.currentTime + duration);
  } catch { /* ignore */ }
}

function playPour():    void { playTone(520, 0.18, 0.15); }
function playDone():    void { playTone(660, 0.12, 0.2); setTimeout(() => playTone(880, 0.2, 0.2), 100); }
function playInvalid(): void { playTone(200, 0.12, 0.12); }

// ── Canvas setup ──────────────────────────────────────────────────────────
const canvas = document.getElementById('game') as HTMLCanvasElement;
const ctx    = canvas.getContext('2d')!;
let W = 0, H = 0;

function resize(): void {
  const w = window.innerWidth;
  const h = window.innerHeight;
  if (!w || !h) return;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  W = w; H = h;
  canvas.width  = W * dpr;
  canvas.height = H * dpr;
  canvas.style.width  = `${W}px`;
  canvas.style.height = `${H}px`;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}
resize();
new ResizeObserver(resize).observe(document.body);
document.addEventListener('visibilitychange', () => { if (!document.hidden) resize(); });
window.addEventListener('pageshow', (e) => { if (e.persisted) resize(); });

// ── Progress (localStorage; ?level=N dev-hook does not persist) ─────────────
const LEVEL_KEY = 'game-water-sort-level';
const levelParam = new URLSearchParams(location.search).get('level');

function loadProgress(): number {
  if (levelParam) {
    const p = parseInt(levelParam, 10) - 1; // 1-based hook
    return Number.isFinite(p) && p >= 0 ? p : 0;
  }
  const s = localStorage.getItem(LEVEL_KEY);
  const n = s ? parseInt(s, 10) : 0;
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function saveProgress(level: number): void {
  if (levelParam || sceneId) return; // dev-хук / кампанийная сцена: прогресс не пишем
  try { localStorage.setItem(LEVEL_KEY, String(level)); } catch { /* ignore */ }
}

// ── Campaign landing (?scene) ───────────────────────────────────────────────
const scene = sceneId ? SCENES[sceneId] ?? null : null;
const campaign = !!scene;
let campaignFlow = campaign;
let firstMoveTracked = false;
let winTracked = false;
let ctaShown = false;

function makeSeeded(): GameState {
  return {
    tubes: scene!.tubes.map(tube => [...tube]),
    selected: null,
    phase: 'playing',
    anim: null,
    history: [],
    level: 0,
    shake: null,
  };
}

// ── State ─────────────────────────────────────────────────────────────────
let state: GameState = campaign ? makeSeeded() : createState(loadProgress());
const theme = readTheme();
let layout: Layout = computeLayout(W, H, state.tubes.length);

// ── Analytics (события раунда) ────────────────────────────────────────────
let ended = false; // level_complete шлём один раз на уровень
function reportStart(): void {
  ended = false;
  track('game_start');
}
reportStart(); // первый уровень (boot)
let pendingStart = !campaign;   // кампанийная посадка сразу интерактивна (крючок сверху)
let menuOpen = false;

// ── Tap handling ──────────────────────────────────────────────────────────
const ANIM_DURATION = 350;
const MAX_HISTORY   = 5;

function hitCircle(x: number, y: number, cx: number, cy: number, r: number): boolean {
  return (x - cx) ** 2 + (y - cy) ** 2 <= r ** 2;
}

function hitRect(x: number, y: number, bx: number, by: number, bw: number, bh: number): boolean {
  return x >= bx && x <= bx + bw && y >= by && y <= by + bh;
}

attachTapListener(canvas, (x, y) => {
  layout = computeLayout(W, H, state.tubes.length);
  const now = performance.now();

  if (pendingStart) {
    audioCtx.resume();
    pendingStart = false;
    return;
  }

  // Menu (☰) tap-intercept — before all other game logic
  const m = menuRects(layout);
  if (menuOpen) {
    if (hitRect(x, y, m.resume.x, m.resume.y, m.resume.w, m.resume.h)) { menuOpen = false; return; }
    if (hitRect(x, y, m.hub.x, m.hub.y, m.hub.w, m.hub.h)) { location.href = document.body.dataset.hubUrl ?? '/'; return; }
    menuOpen = false; return;   // тап мимо пунктов — закрыть
  }
  if (state.phase === 'playing' && hitCircle(x, y, m.toggle.cx, m.toggle.cy, m.toggle.r)) {
    menuOpen = true; return;
  }

  // Win overlay button
  if (state.phase === 'won') {
    const b = winBtnBounds(W, H);
    if (hitRect(x, y, b.x, b.y, b.w, b.h)) {
      const nextLevel = state.level + 1;
      saveProgress(nextLevel);
      state = createState(nextLevel);
      layout = computeLayout(W, H, state.tubes.length);
      reportStart();
    }
    return;
  }

  // Block interaction during animation
  if (state.anim) return;

  // Undo button
  const undo = undoBtnCenter(layout);
  if (hitCircle(x, y, undo.cx, undo.cy, undo.r)) {
    if (state.history.length > 0) {
      const prev = state.history[state.history.length - 1];
      state = {
        ...state,
        tubes: prev,
        history: state.history.slice(0, -1),
        selected: null,
        shake: null,
        anim: null,
      };
    }
    return;
  }

  // Restart button
  const restart = restartBtnCenter(layout);
  if (hitCircle(x, y, restart.cx, restart.cy, restart.r)) {
    state = createState(state.level);
    layout = computeLayout(W, H, state.tubes.length);
    reportStart();
    return;
  }

  const tubeIdx = tubeAtPoint(x, y, layout);

  if (tubeIdx === null) {
    state = { ...state, selected: null };
    return;
  }

  const { selected, tubes } = state;

  if (selected === null) {
    // Select tube (only if not empty)
    if (tubes[tubeIdx].length > 0) {
      state = { ...state, selected: tubeIdx, shake: null };
    }
    return;
  }

  if (selected === tubeIdx) {
    // Deselect
    state = { ...state, selected: null };
    return;
  }

  if (canPour(tubes, selected, tubeIdx)) {
    // Save history
    const snapshot = tubes.map(t => [...t]);
    const history  = [...state.history, snapshot].slice(-MAX_HISTORY);

    const { newTubes, movedColors } = applyPour(tubes, selected, tubeIdx);

    playPour();

    if (campaignFlow && !firstMoveTracked) {
      firstMoveTracked = true;
      ctrack('campaign_first_move');
    }

    state = {
      ...state,
      tubes: newTubes,
      selected: null,
      shake: null,
      history,
      phase: 'playing',   // win is detected after animation completes
      anim: {
        fromIdx: selected,
        toIdx: tubeIdx,
        colors: movedColors,
        startMs: now,
        durationMs: ANIM_DURATION,
      },
    };
  } else {
    // Invalid pour — shake destination, switch selection
    playInvalid();
    state = {
      ...state,
      selected: tubes[tubeIdx].length > 0 ? tubeIdx : null,
      shake: { idx: tubeIdx, startMs: now },
    };
  }
});

// ── Campaign overlays (hook + install CTA) ──────────────────────────────────
const hookOverlay = document.getElementById('hook-overlay') as HTMLDivElement;
const hookTitle   = document.getElementById('hook-title')   as HTMLDivElement;
const hookPlay    = document.getElementById('hook-play')    as HTMLButtonElement;
const ctaOverlay  = document.getElementById('cta-overlay')  as HTMLDivElement;
const ctaTitle    = document.getElementById('cta-title')    as HTMLDivElement;
const ctaInstall  = document.getElementById('cta-install')  as HTMLButtonElement;
const ctaAgain    = document.getElementById('cta-again')    as HTMLButtonElement;

if (campaign && scene) {
  hookTitle.textContent  = t(scene.headlineKey);
  hookPlay.textContent   = t('hook_play');
  ctaTitle.textContent   = t('cta_win_title');
  ctaInstall.textContent = t('cta_install');
  ctaAgain.textContent   = t('cta_again');
  hookOverlay.classList.add('open');
  ctrack('campaign_land');
}

hookPlay.addEventListener('click', (e) => {
  e.stopPropagation();
  hookOverlay.classList.remove('open');
});

ctaInstall.addEventListener('click', (e) => {
  e.stopPropagation();
  promptInstall();
});

ctaAgain.addEventListener('click', (e) => {
  e.stopPropagation();
  ctrack('campaign_continue');
  campaignFlow = false;
  ctaOverlay.classList.remove('open');
  state = createState(0);
  layout = computeLayout(W, H, state.tubes.length);
  reportStart();
});

// ── RAF loop ──────────────────────────────────────────────────────────────
function loop(now: number): void {
  try {
    layout = computeLayout(W, H, state.tubes.length);

    // Expire animation → check win
    if (state.anim && now - state.anim.startMs >= state.anim.durationMs + 30) {
      state = { ...state, anim: null };
      if (isWon(state.tubes)) {
        state = { ...state, phase: 'won' };
        menuOpen = false;
        playDone();
        if (!ended) { ended = true; track('level_complete', { level: state.level }); }
      }
    }

    // Кампанийная воронка: победа на засеянной доске → показать install-CTA.
    if (campaignFlow && state.phase === 'won') {
      if (!winTracked) {
        winTracked = true;
        ctrack('campaign_seeded_win');
      }
      if (!ctaOverlay.classList.contains('open')) {
        ctaInstall.style.display = canInstall() ? '' : 'none';
        ctaOverlay.classList.add('open');
        if (!ctaShown) {
          ctaShown = true;
          ctrack('campaign_install_prompt', { install_available: canInstall() });
        }
      }
    }

    // Expire shake
    if (state.shake && now - state.shake.startMs >= 350) {
      state = { ...state, shake: null };
    }

    renderFrame(ctx, W, H, layout, state, theme, now, pendingStart, menuOpen);
  } catch (e) {
    console.error('[water-sort] loop error', e);
  }
  requestAnimationFrame(loop);
}

requestAnimationFrame(loop);
